// TODO: rdar://104038831 (Share Locale logic between music + podcast)
import { getContext } from 'svelte';
import { DEFAULT_STOREFRONT_CODE } from '~/constants/storefront';
import { regions } from '~/utils/storefront-data';
import { setLocale as setSharedLocale } from '@amp/web-app-components/src/utils/locale';
import { languages } from '~/utils/storefront-data';

const CONTEXT_NAME = 'locale';

type UnnormalizedLocale = {
    storefront: string | null;
    language: string | null;
};

export class Locale {
    /**
     * Normalize an optional requested storefront and language to a supported
     * storefront and language pair (locale).
     *
     * For example us/en is normalized to us/en-US.
     *
     * expandDefaultLanguage causes the language normalized language to
     * always be returned. If false (or not present), null will be returned in
     * the case that the default language is chosen. This is to facilitate
     * deciding whether to add an ?l param to a URL. If null, then the ?l
     * param should not be added.
     *
     * For example, with expandDefaultLanguage set, us/en is normalized to
     * us/en-US. But without it, us/en is normalized to us/null.
     */
    static normalize({ storefront, language }: UnnormalizedLocale): {
        storefront: string;
        language: string;
        isDefaultLanguage: boolean;
    } {
        const {
            storefront: normalizedStorefront,
            languages,
            defaultLanguage,
        } = normalizeStorefront(storefront);
        return {
            storefront: normalizedStorefront,
            ...normalizeLanguage(language || '', languages, defaultLanguage),
        };
    }

    /**
     * Generates the correct storefront and language that should be used for a canonical URL.
     *
     * @returns an object containing `storefront` and `language` to use in URL.
     * If the language is the storefront default it will return `null` for language.
     *
     * @example
     * ```ts
     *  const locale = { storefront: 'us' language: 'en-US' }
     *
     *  // return { storefront: 'us', language: null }
     *  const canonicalLocale = Locale.canonicalize(locale);
     * ```
     * @example
     * ```ts
     *  const locale = { storefront: 'us' language: 'es-MX' }
     *
     *  // return { storefront: 'us', language: 'es' }
     *  const canonicalLocale = Locale.canonicalize(locale);
     * ```
     */
    static canonicalize(unnormalizedLocale: UnnormalizedLocale): {
        storefront: string;
        language: string | null;
    } {
        const { storefront, language, isDefaultLanguage } =
            Locale.normalize(unnormalizedLocale);

        // We should split off the language part of the language (ex. en in
        // en-US) so that canonical URLs generated by the app are
        // correct (/us/browse?l=es instead of /us/browse?l=es-MX).
        //
        // In the case there are multiple dialects of the same language (ex. zh-hans-ch and zh-hant-tw)
        // we should maintain the entire language string instead of splitting the string.
        const shouldMaintainLanguageTag = languages[storefront].some(
            (supportedLanguage) =>
                language !== supportedLanguage.tag &&
                supportedLanguage.tag?.split('-')[0] === language.split('-')[0],
        );
        const normalizedLanguage = isDefaultLanguage
            ? null
            : shouldMaintainLanguageTag
            ? language
            : language.split('-')[0];

        return {
            storefront,
            language: normalizedLanguage,
        };
    }

    static load(
        context: Map<string, unknown>,
        storefront: string,
        language: string,
    ): Locale {
        const locale = new Locale(storefront, language);

        context.set(CONTEXT_NAME, locale);

        // WARNING: this signature can change after rdar://93379311
        setSharedLocale(context, locale);

        return locale;
    }

    readonly storefront: string;
    readonly language: string;

    private constructor(storefront: string, language: string) {
        this.storefront = storefront;
        this.language = language;
    }
}

function normalizeStorefront(storefront: string | null): {
    storefront: string;
    languages: string[];
    defaultLanguage: string;
} {
    const storefronts = {};

    for (const { locales } of regions) {
        for (const { id, language, isDefault } of locales) {
            if (isDefault) {
                storefronts[id] = {
                    languages: [],
                    defaultLanguage: language,
                };
            }

            if (id in storefronts) {
                storefronts[id].languages.push(language);
            }
        }
    }

    const normalizedStorefront = (storefront || '').toLowerCase();
    const chosenStorefront =
        normalizedStorefront in storefronts
            ? normalizedStorefront
            : DEFAULT_STOREFRONT_CODE;

    return {
        storefront: chosenStorefront,
        ...storefronts[chosenStorefront],
    };
}

function normalizeLanguage(
    language: string,
    languages: string[],
    defaultLanguage: string,
): { language: string; isDefaultLanguage: boolean } {
    function annotateReturn(language: string): {
        language: string;
        isDefaultLanguage: boolean;
    } {
        return {
            language,
            isDefaultLanguage: language === defaultLanguage,
        };
    }

    // Media API is incorrectly providing us with iw as a language in canonical URLs
    // For translations to work we need to use the updated he language tag for Hebrew
    // rdar://103555559 (iPS Ticket, P3: Apple Music - ISR - Website does not use consistent
    // locale format (iw vs. he) & causes an unlocalized experience with deep links)
    if (language === 'iw') {
        language = 'he';
    }

    // Prefer an exact match (ex. en-US matches en-US)
    const exactMatch = findMatch(language, languages, (a, b) => a === b);
    if (exactMatch) {
        return annotateReturn(exactMatch);
    }

    // Try partial match (ex. fr-CA or fr matches fr-FR)
    const partialMatch = findMatch(
        language,
        languages,
        (a, b) => a.split('-')[0] === b.split('-')[0],
    );
    if (partialMatch) {
        return annotateReturn(partialMatch);
    }

    // The only remaining choice is the storefront default
    return annotateReturn(defaultLanguage);
}

function findMatch(
    needle: string,
    haystack: string[],
    matches: (a: string, b: string) => boolean,
): string | null {
    return haystack.find((possibility) =>
        matches(possibility.toLowerCase(), needle.toLowerCase()),
    );
}

/**
 * Gets the current Locale instance from the Svelte context.
 *
 * @return locale The current instance of Locale
 */
export function getLocale(): Locale {
    const locale = getContext(CONTEXT_NAME) as Locale | undefined;

    if (!locale) {
        throw new Error('getLocale called before Locale.load');
    }

    return locale;
}
